import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Form, FormProvider } from 'react-hook-form';

import {
  DatepickerFormField,
  NumberInputFormField,
  TimeOffUserSearchFormField,
} from 'components/DynamicFormFields';
import { CustomModal, SaveChangesModal } from '@components';
import { useAddTimeOff } from '../hooks';
import { isEmpty } from 'lodash';

interface AddTimeOffProps {
  variant: 'edit' | 'create';
  disabled?: boolean;
  time_off_id?: string;
}

export const AddTimeOff: FC<AddTimeOffProps> = ({
  variant,
  time_off_id = '',
  disabled = false,
}) => {
  const {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    onStartEndDateChange,
    isLoading,
  } = useAddTimeOff(time_off_id, variant);

  const { control } = formMethods;

  return (
    <>
      {variant === 'create' ? (
        <Button
          onClick={handleOpen}
          startIcon={<Add />}
          sx={{
            minHeight: '30px',
            fontWeight: 'regular',
            fontSize: '1rem',
            color: 'white',
          }}
          color='secondary'
          disabled={disabled}
          variant='contained'
        >
          ADD TIME OFF
        </Button>
      ) : (
        <IconButton
          sx={{
            '& .MuiIconButton': {
              padding: '0.5rem',
            },
          }}
          disabled={disabled}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            handleOpen();
          }}
        >
          <Edit color={disabled ? 'disabled' : 'primary'} />
        </IconButton>
      )}
      <Box>
        <CustomModal
          isOpen={isOpen && !disabled}
          onClose={handleClose}
          isLoading={isSubmitting}
          title={variant === 'create' ? 'Add time off' : 'Edit time off'}
        >
          <FormProvider {...formMethods}>
            <Form
              control={control}
              onSubmit={e => onSubmit(e.event)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {isLoading && variant === 'edit' ? (
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    height: 203,
                  }}
                >
                  <CircularProgress
                    sx={{ height: 32, width: 32 }}
                    color='primary'
                  />
                </Box>
              ) : (
                <>
                  <TimeOffUserSearchFormField
                    name={'user_id'}
                    optional={false}
                    disabled={!isEmpty(time_off_id)}
                  />
                  <Box sx={{ width: '100%', display: 'flex', gap: '1rem' }}>
                    <DatepickerFormField
                      name='start_date'
                      label='Start date'
                      optional={false}
                      onChangeCallback={onStartEndDateChange}
                    />

                    <DatepickerFormField
                      name='end_date'
                      label='End date'
                      optional={false}
                      onChangeCallback={onStartEndDateChange}
                    />
                  </Box>

                  <NumberInputFormField
                    name='days'
                    label='Days'
                    optional={false}
                    float
                  />
                </>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  paddingY: 3,
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  zIndex: 3,
                  background: '#fff',
                }}
              >
                <Button
                  variant='text'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                  }}
                  disabled={isSubmitting || isLoading}
                  onClick={handleClose}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'white',
                  }}
                  disabled={
                    isSubmitting || isEditSubmitting || !isValid || isLoading
                  }
                  loading={isSubmitting || isEditSubmitting}
                  loadingPosition='start'
                >
                  {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
                </Button>
              </Box>
              <SaveChangesModal
                isOpen={isSaveChangesModalOpen}
                onClose={handleCloseSaveChangesModal}
                isValid={isValid}
                handleSaveChanges={handleSaveChanges}
                discardChanges={handleDiscardChanges}
                isLoading={isEditSubmitting}
              />
            </Form>
          </FormProvider>
        </CustomModal>
      </Box>
    </>
  );
};
